'use client';

import { RAIL_MENU_ROW, RAIL_MENU_ACTIVE, RAIL_MENU_LABEL } from './rail-menu-styles';

import { Box, Button, Flex, IconButton, Link, Text, VStack } from '@chakra-ui/react';
import { IconChartBar, IconStar, type TablerIcon } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useProject } from '@/contexts/ProjectContext';
import { getIsNavActive } from './Sidebar';
import {
  getAppNavigationSections,
  getBrandSlugFromPathname,
  getResolvedNavHref,
  type DataExplorerPreset,
} from './navigation';
import { TOP_NAV_FAVORITE_LABEL_OVERRIDES, useTopNavFavorites } from './useTopNavFavorites';
import { useDataExplorerBrandFavorites } from './useDataExplorerBrandFavorites';

const PRESET_STORAGE_KEY = 'attune:data-explorer-preset:v1';

export function DataRailPanel() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { currentProject } = useProject();
  const [preset, setPreset] = useState<DataExplorerPreset>('brand');
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(PRESET_STORAGE_KEY);
      if (stored === 'brand' || stored === 'publisher') setPreset(stored);
    } catch {
      // The menu still works when browser storage is unavailable.
    }
  }, []);
  const sections = getAppNavigationSections(preset).filter(section => section.title !== 'Home');
  const items = sections.flatMap(section => section.items);
  const { favoriteHrefs, toggleFavorite } = useTopNavFavorites(items.map(item => item.href).sort());
  const { favorites: brands } = useDataExplorerBrandFavorites(currentProject?.id);
  const defaultBrandSlug = currentProject?.brand_settings?.brand_slug;
  const navBrandSlug = getBrandSlugFromPathname(pathname) ?? defaultBrandSlug;
  const overviewHref = preset === 'publisher' ? '/app/publishers' : '/app/data-explorer';
  const favoriteItems = items.filter(item => favoriteHrefs.includes(item.href));
  const activeHref = items
    .filter(item =>
      getIsNavActive({ pathname, searchParams, href: item.href, defaultBrandSlug, navBrandSlug })
    )
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;
  const changePreset = (next: DataExplorerPreset) => {
    setPreset(next);
    try {
      window.localStorage.setItem(PRESET_STORAGE_KEY, next);
    } catch {
      // Keep the selected preset for this session even when storage is unavailable.
    }
  };
  function row(item: { href: string; label: string; icon: TablerIcon }, favorite = false) {
    const ItemIcon = item.icon;
    const active = item.href === activeHref;
    const starred = favoriteHrefs.includes(item.href);
    return (
      <Flex
        key={item.href}
        className="group"
        align="center"
        minH="36px"
        borderRadius="8px"
        bg={active ? 'ui.surface.selected' : 'transparent'}
        _hover={{ bg: 'ui.surface.rowHover' }}
      >
        <Link
          as={NextLink}
          href={getResolvedNavHref({
            href: item.href,
            brandSlug: navBrandSlug,
            searchParams,
            pathname,
          })}
          aria-current={active ? 'page' : undefined}
          flex="1"
          {...RAIL_MENU_ROW}
          w="auto"
          h="auto"
          py="2"
          title={item.label}
          {...(active ? RAIL_MENU_ACTIVE : {})}
        >
          <ItemIcon size={16} stroke={1.6} style={{ flexShrink: 0 }} />
          <Text fontSize="14px" lineHeight="20px" fontWeight="500" lineClamp={2}>
            {favorite ? TOP_NAV_FAVORITE_LABEL_OVERRIDES[item.href] || item.label : item.label}
          </Text>
        </Link>
        <IconButton
          size="2xs"
          variant="ghost"
          mr="1"
          flexShrink={0}
          aria-label={`${starred ? 'Remove' : 'Add'} ${item.label} ${starred ? 'from' : 'to'} favorites`}
          aria-pressed={starred}
          onClick={() => toggleFavorite(item.href)}
          opacity={starred ? 0.65 : 0.35}
          _groupHover={{ opacity: 1 }}
          _focusVisible={{ opacity: 1 }}
          color="ui.text.tertiary"
        >
          <IconStar size={13} stroke={1.6} fill={starred ? 'currentColor' : 'none'} />
        </IconButton>
      </Flex>
    );
  }
  return (
    <VStack as="nav" aria-label="Data navigation" align="stretch" gap="4" px="0" pt="1" pb="6">
      <Box position="sticky" top="0" zIndex={2} bg="ui.surface.card" py="1">
        <Flex
          role="group"
          aria-label="Menu preset"
          p="0.5"
          gap="0.5"
          bg="ui.surface.input"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          borderRadius="9px"
        >
          {(['brand', 'publisher'] as const).map(option => (
            <Button
              key={option}
              variant="plain"
              flex="1"
              h="30px"
              minW="0"
              borderRadius="7px"
              bg={preset === option ? 'ui.surface.card' : 'transparent'}
              color={preset === option ? 'ui.text.primary' : 'ui.text.tertiary'}
              fontSize="xs"
              fontWeight={preset === option ? '700' : '500'}
              aria-pressed={preset === option}
              onClick={() => changePreset(option)}
            >
              {option === 'brand' ? 'Brand' : 'Publisher'}
            </Button>
          ))}
        </Flex>
      </Box>
      <Box>
        <Text px="3" pb="2" {...RAIL_MENU_LABEL}>
          Favorites
        </Text>
        <VStack align="stretch" gap="0.5">
          {favoriteItems.map(item => row(item, true))}
          {brands.map(brand => (
            <Link key={brand.slug} as={NextLink} href={brand.href} {...RAIL_MENU_ROW}>
              <IconStar size={16} stroke={1.6} style={{ flexShrink: 0 }} />
              <Text fontSize="14px" lineHeight="20px" fontWeight="500" truncate>
                {brand.name}
              </Text>
            </Link>
          ))}
          {!favoriteItems.length && !brands.length ? (
            <Text px="3" fontSize="xs" color="ui.text.tertiary">
              Star a page to keep it here.
            </Text>
          ) : null}
        </VStack>
      </Box>
      <Box
        data-section="dataExplorerOverview"
        borderTopWidth="1px"
        borderColor="ui.border.subtle"
        pt="3"
      >
        <Link
          as={NextLink}
          href={getResolvedNavHref({
            href: overviewHref,
            brandSlug: navBrandSlug,
            searchParams,
            pathname,
          })}
          aria-current={pathname === overviewHref ? 'page' : undefined}
          {...RAIL_MENU_ROW}
          {...(pathname === overviewHref ? RAIL_MENU_ACTIVE : {})}
        >
          <IconChartBar size={16} stroke={1.6} />{' '}
          {preset === 'publisher' ? 'Publisher Overview' : 'Brand Overview'}
        </Link>
      </Box>
      {sections.map(section => {
        const id = `data-nav-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        return (
          <Box as="section" key={section.title} aria-labelledby={id}>
            <Text
              as="h2"
              id={id}
              px="3"
              mb="1"
              {...RAIL_MENU_LABEL}
              fontWeight="700"
              color="ui.text.primary"
            >
              {section.title}
            </Text>
            <VStack align="stretch" gap="0.5">
              {section.items.map(item => row(item))}
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
}
